import { useEffect, useState } from "react";
import { getApiService } from "../services/api";
import type { ApiJob } from "../services/api";

interface Props {
  open: boolean;
  job: ApiJob | null; // 编辑时传入，新建为 null
  onClose: () => void;
  onSave: (data: {
    name: string;
    cron: string;
    task: { [key: string]: string[] };
    status?: "running" | "stopped";
  }) => Promise<void> | void;
}

const CRON_PRESETS = [
  { label: "每分钟", value: "* * * * *" },
  { label: "每 5 分钟", value: "*/5 * * * *" },
  { label: "每小时", value: "0 * * * *" },
  { label: "每天", value: "0 0 * * *" },
  { label: "工作日 15:30", value: "30 15 * * 1-5" },
];

// 新建 / 编辑任务对话框
export function JobDialog({ open, job, onClose, onSave }: Props) {
  const [name, setName] = useState("");
  const [cron, setCron] = useState("");
  const [module, setModule] = useState("");
  const [params, setParams] = useState("");
  const [modules, setModules] = useState<string[]>([]);
  const [paramsError, setParamsError] = useState("");
  const [busy, setBusy] = useState(false);

  // 加载任务模块列表
  useEffect(() => {
    if (!open) return;
    getApiService()
      .getJobTasks()
      .then(setModules)
      .catch(() => setModules([]));
  }, [open]);

  // 编辑时回填
  useEffect(() => {
    if (!open) return;
    if (job) {
      setName(job.name ?? "");
      setCron(job.cron);
      const entries = Object.entries(job.task ?? {});
      const first = entries[0];
      if (first) {
        setModule(first[0]);
        setParams(JSON.stringify(first[1]));
      } else {
        setModule("");
        setParams("");
      }
    } else {
      setName("");
      setCron(CRON_PRESETS[0]!.value);
      setModule("");
      setParams("[]");
    }
    setParamsError("");
  }, [open, job]);

  if (!open) return null;

  const parseParams = (): string[] | null => {
    try {
      const v = JSON.parse(params || "[]");
      if (!Array.isArray(v)) return null;
      return v.map(String);
    } catch {
      return null;
    }
  };

  const submit = async () => {
    if (!name.trim() || !cron.trim() || !module) return;
    const parsed = parseParams();
    if (parsed == null) {
      setParamsError("参数需为 JSON 数组格式，如 [\"hello\"]");
      return;
    }
    setBusy(true);
    try {
      await onSave({
        name: name.trim(),
        cron: cron.trim(),
        task: { [module]: parsed },
      });
      onClose();
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="dialog-overlay" onMouseDown={onClose}>
      <div className="dialog" onMouseDown={(e) => e.stopPropagation()}>
        <div className="dialog-title">{job ? "编辑任务" : "新建任务"}</div>

        <div className="mb-3">
          <label className="label">任务名称</label>
          <input
            className="input"
            placeholder="请输入任务名称"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </div>

        <div className="mb-3">
          <label className="label">Cron 表达式</label>
          <input
            className="input"
            placeholder="* * * * *"
            value={cron}
            onChange={(e) => setCron(e.target.value)}
          />
          <div className="flex flex-wrap gap-2 mt-2">
            {CRON_PRESETS.map((p) => (
              <button
                key={p.value}
                className={`chip ${cron === p.value ? "chip-active" : ""}`}
                onClick={() => setCron(p.value)}
              >
                {p.label}
              </button>
            ))}
          </div>
        </div>

        <div className="mb-3">
          <label className="label">任务模块</label>
          <select
            className="input"
            value={module}
            onChange={(e) => setModule(e.target.value)}
          >
            <option value="">请选择模块</option>
            {modules.map((m) => (
              <option key={m} value={m}>
                {m}
              </option>
            ))}
          </select>
        </div>

        <div className="mb-3">
          <label className="label">任务参数（JSON 数组格式）</label>
          <input
            className="input"
            placeholder='["参数1", "参数2"]'
            value={params}
            onChange={(e) => {
              setParams(e.target.value);
              setParamsError("");
            }}
          />
          {paramsError && <div className="down mt-1 text-12px">{paramsError}</div>}
        </div>

        <div className="flex gap-2 justify-end mt-4">
          <button className="btn" onClick={onClose}>
            取消
          </button>
          <button
            className="btn btn-primary"
            disabled={busy || !name.trim() || !cron.trim() || !module}
            onClick={submit}
          >
            保存
          </button>
        </div>
      </div>
    </div>
  );
}
